import { Button } from "@/components/ui/button";
import { useTranslation } from "react-i18next";
import { ShoppingCart, Lock, BookOpen } from "lucide-react";
import { LectureContent } from "../types/lectureTypes";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";

interface LectureViewWithoutPermissionProps {
  content: LectureContent;
}

export function LectureViewWithoutPermission({
  content,
}: LectureViewWithoutPermissionProps) {
  const { t } = useTranslation();
  const handleAddToCart = () => {
    // window.location.href = `/courses/${courseId}`;
  };

  return (
    <div className="h-full flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800 p-6">
      <Card className="max-w-2xl w-full shadow-xl">
        <CardHeader className="text-center space-y-4">
          <div className="mx-auto w-20 h-20 bg-primary-100 dark:bg-primary-950/50 rounded-full flex items-center justify-center">
            <Lock className="w-10 h-10 text-primary-600 dark:text-primary-500" />
          </div>
          <CardTitle className="text-3xl font-bold">
            {t("lecture.locked.title")}
          </CardTitle>
          <CardDescription className="text-lg">
            {t("lecture.locked.description")}
          </CardDescription>
        </CardHeader>

        <CardContent className="space-y-6">
          {/* Lecture Info */}
          <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-6 space-y-3">
            <div className="flex items-start gap-3">
              <BookOpen className="w-5 h-5 text-gray-600 dark:text-gray-400 mt-0.5 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <h3 className="font-semibold text-gray-900 dark:text-white text-lg">
                  {content.name}
                </h3>
                {content.description && (
                  <p className="text-gray-600 dark:text-gray-400 mt-2 text-sm leading-relaxed">
                    {content.description}
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Benefits */}
          <div className="space-y-3">
            <h4 className="font-semibold text-gray-900 dark:text-white">
              {t("lecture.locked.benefitsTitle")}
            </h4>
            <ul className="space-y-2">
              <li className="flex items-center gap-3 text-gray-700 dark:text-gray-300">
                <div className="w-1.5 h-1.5 bg-primary rounded-full flex-shrink-0" />
                <span>{t("lecture.locked.benefits.unlimitedAccess")}</span>
              </li>
              <li className="flex items-center gap-3 text-gray-700 dark:text-gray-300">
                <div className="w-1.5 h-1.5 bg-primary rounded-full flex-shrink-0" />
                <span>{t("lecture.locked.benefits.progressCertificate")}</span>
              </li>
              <li className="flex items-center gap-3 text-gray-700 dark:text-gray-300">
                <div className="w-1.5 h-1.5 bg-primary rounded-full flex-shrink-0" />
                <span>{t("lecture.locked.benefits.resources")}</span>
              </li>
              <li className="flex items-center gap-3 text-gray-700 dark:text-gray-300">
                <div className="w-1.5 h-1.5 bg-primary rounded-full flex-shrink-0" />
                <span>{t("lecture.locked.benefits.lifetimeUpdates")}</span>
              </li>
            </ul>
          </div>

          {/* CTA Button */}
          <div className="pt-4">
            <Button
              onClick={handleAddToCart}
              className="w-full bg-primary hover:bg-primary/90 text-white h-12 text-lg font-semibold shadow-lg hover:shadow-xl transition-all"
            >
              <ShoppingCart className="w-5 h-5 mr-2" />
              {t("lecture.locked.purchaseCourse")}
            </Button>
          </div>

          {/* Additional Info */}
          <p className="text-center text-sm text-gray-500 dark:text-gray-400">
            {t("lecture.locked.courseDetailsPrefix")}{" "}
            <button
              onClick={handleAddToCart}
              className="text-primary-600 hover:text-primary-700 dark:text-primary-500 dark:hover:text-primary-400 font-medium underline"
            >
              {t("lecture.locked.clickHere")}
            </button>
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
